/**
 * Site-wide smooth scrolling, by Lenis.
 *
 * Lenis animates the real document scroll — it never transforms a wrapper the
 * way Locomotive or ScrollSmoother do — so `document.body` keeps its natural
 * height, `window.scrollY` stays true, `position: sticky` keeps working and the
 * footer stays reachable however the page height changes after load. That is
 * also why nothing here has to be told about new content: Lenis watches
 * `documentElement` with a ResizeObserver, so anything the site swaps in — the
 * AJAX post grid, an opened accordion, a late-loading image — remeasures on its
 * own.
 *
 * Two options carry most of the integration:
 *
 * - `allowNestedScroll` lets a scrollable element under the pointer take the
 *   wheel itself, so the mobile menu card, the Quick Quote panel and the
 *   horizontal card rails scroll natively without any markup of their own.
 * - `anchors` hands in-page links to Lenis. It reads the target's
 *   `scroll-margin-top`, so every jump still lands clear of the fixed header
 *   exactly where the CSS says it should.
 *
 * Touch is deliberately left alone: phones and tablets keep native momentum
 * scrolling, which is what makes the snapping card rails behave there.
 * `prefers-reduced-motion` is honoured by Lenis itself (`respectReducedMotion`
 * is on by default), which is better than bailing out — the instance still
 * exists, so anchors and `scrollTo` callers keep working, they just stop
 * animating.
 */

import Lenis from 'lenis';

/**
 * Exponential ease-out — Lenis's own recommended curve.
 *
 * Paired with `duration` rather than `lerp` on purpose. `lerp` decays towards
 * the target asymptotically, so the last stretch of every glide moves less than
 * one pixel per frame. The browser can only paint whole device pixels, so those
 * frames paint nothing at all and the page appears to stick, then twitch.
 * Measured on this site at `devicePixelRatio: 1`, `lerp: 0.08` spent up to 13
 * consecutive frames (~217ms) painting no movement; this curve, which lands on
 * a fixed schedule instead of creeping, cuts that to 6 frames (~100ms) for the
 * same overall glide length. The effect roughly halves again at
 * `devicePixelRatio: 2`, which is why it reads as a Windows problem.
 *
 * @param {number} t Linear progress, 0-1.
 * @return {number} Eased progress.
 */
const EXPO_OUT = ( t ) => Math.min( 1, 1.001 - Math.pow( 2, -10 * t ) );

/**
 * The classes the site's overlays set while they hold the page.
 *
 * The Quick Quote modal marks the root, the mobile menu marks the body. Both
 * already stop the page scrolling in CSS; stopping Lenis as well keeps its
 * internal position in step with the document so nothing jumps when the
 * overlay closes.
 *
 * @type {Array<{ el: () => HTMLElement, className: string }>}
 */
const OVERLAYS = [
	{ el: () => document.documentElement, className: 'bf-qq-open' },
	{ el: () => document.body, className: 'has-menu-open' },
];

/**
 * Freeze the page behind an open overlay, and release it again.
 *
 * `lenis.stop()` adds `.lenis-stopped` to the root, which Lenis's own
 * stylesheet turns into `overflow: clip`. It also makes Lenis swallow the
 * wheel — but only after its nested-scroll check has run, so the panel inside
 * the overlay still scrolls natively. That is the whole reason
 * `allowNestedScroll` has to stay on.
 *
 * @param {Lenis} lenis The instance.
 * @return {void}
 */
function lockWithOverlays( lenis ) {
	const isOpen = () =>
		OVERLAYS.some( ( { el, className } ) => el()?.classList.contains( className ) );

	let locked = false;

	const sync = () => {
		const open = isOpen();

		if ( open === locked ) {
			return;
		}

		locked = open;

		// A second overlay opening over the first must not release the page,
		// which is why this tracks "any overlay open" rather than counting.
		if ( open ) {
			lenis.stop();
		} else {
			lenis.start();
		}
	};

	const observer = new MutationObserver( sync );
	const options = { attributes: true, attributeFilter: [ 'class' ] };

	observer.observe( document.documentElement, options );
	observer.observe( document.body, options );

	// An overlay could already be open on a bfcache restore.
	sync();
}

export function SmoothScroll() {
	if ( window.bfLenis ) {
		return;
	}

	const lenis = new Lenis( {
		autoRaf: true,
		anchors: true,
		allowNestedScroll: true,
		duration: 1.2,
		easing: EXPO_OUT,
		// Mobile keeps native momentum scrolling; Lenis only smooths the wheel.
		syncTouch: false,
		// Clicking through to another page should not land mid-glide.
		stopInertiaOnNavigate: true,
	} );

	window.bfLenis = lenis;

	lockWithOverlays( lenis );

	// Coming back through the bfcache restores the browser's scroll position
	// without a scroll event, so Lenis has to be told where the page now is.
	// `force` covers a restore that happened while an overlay had it stopped.
	window.addEventListener( 'pageshow', ( event ) => {
		if ( ! event.persisted ) {
			return;
		}

		lenis.resize();
		lenis.scrollTo( window.scrollY, { immediate: true, force: true } );
	} );
}
